import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import CardGrid from '../components/CardGrid.jsx';
import NamedCourseGrid, { buildLayoutRows } from '../components/NamedCourseGrid.jsx';
import CardPropertiesEditor from '../components/CardPropertiesEditor.jsx';
import CourseEditor from '../components/CourseEditor.jsx';
import CoursePreview from '../components/CoursePreview.jsx';
import PasteImport from '../components/PasteImport.jsx';
import PortalHeader from '../components/PortalHeader';
import PublishStateNote from '../components/PublishStateNote';
import {
  getClubMarks,
  getClubEvents,
  getEventBundle,
  saveWip,
  loadWip,
  publishWip,
  getPublishedBundles,
  getEventPublishedBundles,
} from '../lib/firebase.js';
import { computeBundleDiff } from '../lib/bundleDiff';
import { calculateFullDistance } from '../lib/roundingInference';
import { fixedMarkLineBearing } from '../lib/geo.js';
import { canonicalizeMarkIds } from '../lib/coursePreviewLayout';
import { loadFleetConfig } from '../lib/fleetConfigStore';
import { loadCourseGridConfig, saveCourseGridConfig } from '../lib/courseGridConfigStore';
import { loadClubLineDefaults, saveClubLineDefaults, pickLineDefaults } from '../lib/clubLineDefaultsStore';
import {
  DEFAULT_GRID_CONFIG,
  DIRECTION_COUNTS,
  NAME_SCHEMES,
  NUMBERING_MODES,
  buildGridConfig,
  courseNumbersFor,
  defaultCourseName,
  directionLettersFor,
  directionRowsFor,
  isDefaultGridConfig,
} from '../lib/courseGridConfig';
import { normalizeMarks } from '../lib/markNormalize';
import { mergePreviewStartFinish } from '../lib/previewLine';

function buildMarkLookup(marks) {
  const lookup = {};
  for (const m of marks) {
    if (typeof m.latitude === 'number' && typeof m.longitude === 'number' &&
        !(m.latitude === 0 && m.longitude === 0)) {
      lookup[m.id] = [m.latitude, m.longitude];
    }
  }
  return lookup;
}

/** Merge club marks with bundle marks. Club marks take priority; bundle marks fill gaps.
 *  Both sides are normalized to { id, latitude:number, longitude:number } so a non-canonical
 *  shape on either source still renders (and can't be zeroed on save) — ra-or1y.
 *  Dedup is CASE-INSENSITIVE: a bundle 'n' that duplicates a club 'N' must not survive as
 *  an "extra", or canonicalizeMarkIds could later resolve the id to the bundle/stale mark
 *  and override the club one — breaking club priority (ra-or1y, codex review). */
export function mergeMarks(clubMarks, bundleMarks) {
  const club = normalizeMarks(clubMarks);
  const bundle = normalizeMarks(bundleMarks);
  if (bundle.length === 0) return club;
  const seen = new Set(club.map((m) => String(m.id).toUpperCase()));
  const extras = bundle.filter((m) => !seen.has(String(m.id).toUpperCase()));
  return extras.length > 0 ? [...club, ...extras] : club;
}

const DEFAULT_BUNDLE_DEFAULTS = {
  defaultStartType: 'vessel',
  defaultStartOffsetNm: 0.5,
  defaultStartOffsetAngle: 'downwind',
  defaultStartMarkA: '',
  defaultStartMarkB: '',
  defaultFinishType: 'vessel',
  defaultFinishOffsetNm: 0.25,
  defaultFinishOffsetAngle: 'upwind',
  defaultFinishMarkA: '',
  defaultFinishMarkB: '',
};

function newCard(name = '') {
  return {
    id: crypto.randomUUID(),
    name,
    vhf: '',
    footnotes: '',
    sailing_classes: [],
    properties: [],
    startType: null,
    startMarkA: null,
    startMarkB: null,
    startOffsetNm: null,
    startOffsetAngle: null,
    finishType: null,
    finishMarkA: null,
    finishMarkB: null,
    finishOffsetNm: null,
    finishOffsetAngle: null,
    courses: [],
  };
}

function newCourse(directionCode, courseNumber) {
  return {
    id: crypto.randomUUID(),
    directionCode,
    courseNumber,
    markIds: [],
    markDirections: [],
    bearing: 0,
    startType: null,
    startMarkA: null,
    startMarkB: null,
    startOffsetNm: null,
    startOffsetAngle: null,
    finishType: null,
    finishMarkA: null,
    finishMarkB: null,
    finishOffsetNm: null,
    finishOffsetAngle: null,
  };
}

/** A fresh grid course, with a scheme-based default name stamped when the club
 *  runs a customized grid. The DBSC default leaves grid courses nameless so the
 *  flagship club's published bundle stays byte-identical. (ra-gbrd.20) */
function newGridCourse(directionCode, courseNumber, gridConfig) {
  const course = newCourse(directionCode, courseNumber);
  if (!isDefaultGridConfig(gridConfig)) {
    course.name = defaultCourseName(gridConfig, directionCode, courseNumber);
  }
  return course;
}

function newNamedCourse(name) {
  return {
    id: crypto.randomUUID(),
    directionCode: null,
    courseNumber: 0,
    name,
    windDirection: null,
    startArea: null,
    markIds: [],
    markDirections: [],
    bearing: 0,
    startType: null,
    startMarkA: null,
    startMarkB: null,
    startOffsetNm: null,
    startOffsetAngle: null,
    finishType: null,
    finishMarkA: null,
    finishMarkB: null,
    finishOffsetNm: null,
    finishOffsetAngle: null,
  };
}

/**
 * Whether a grid cell is one CardGrid actually draws under this config.
 *
 * A cell can fall off the grid without anyone touching it: the club's grid
 * settings are live on this page (Wind directions, Courses / direction), so
 * shrinking the grid strands an already-selected cell outside it. Membership
 * only — an in-grid cell with no course yet is a perfectly good selection, that
 * is how a course gets created. (ra-h29bt, codex on 2bcb16c)
 */
export function cellIsRendered(cell, gridConfig) {
  if (!cell) return false;
  const row = directionRowsFor(gridConfig).find((r) => r.dir === cell.dir);
  return !!row && row.numbers.includes(cell.num);
}

/**
 * The course a card should open on when it becomes the selected card, or null
 * when it has nothing a user could have clicked. (ra-h29bt)
 *
 * The order is the order the left panel DRAWS the card's courses, so the
 * auto-selected course is the one the eye lands on first: named courses
 * (NamedCourseGrid, rendered above) before grid courses, each in that widget's
 * own layout order. Both orders are borrowed from the widgets themselves —
 * buildLayoutRows and directionRowsFor — rather than restated here, so a change
 * to either layout cannot silently move the auto-selection somewhere the user
 * is not looking.
 *
 * Returns null for a card with no courses, and for one whose only courses are
 * grid courses sitting OUTSIDE the club's configured grid (a card imported
 * under a 16x8 grid, read under an 8x4 one): CardGrid draws no cell for those,
 * so selecting one would open an editor with nothing highlighted to explain it.
 * An empty right pane is the honest answer when the left panel is empty too.
 */
export function firstCourseSelection(card, gridConfig) {
  const courses = card?.courses || [];
  if (courses.length === 0) return null;

  const named = courses.filter((c) => c.directionCode == null);
  if (named.length > 0) {
    const row = buildLayoutRows(named).find((r) => r.courses.length > 0);
    if (row) return { namedCourseId: row.courses[0].id, cell: null };
  }

  const grid = courses.filter((c) => c.directionCode != null);
  if (grid.length === 0) return null;
  for (const row of directionRowsFor(gridConfig)) {
    for (const num of row.numbers) {
      if (grid.some((c) => c.directionCode === row.dir && c.courseNumber === num)) {
        return { namedCourseId: null, cell: { dir: row.dir, num } };
      }
    }
  }
  return null;
}

/** Normalize legacy 'offset' type to 'vessel'. */
function normalizeLineType(type) {
  return type === 'offset' ? 'vessel' : type;
}

/**
 * Normalize offset angle for persistence. The shipped app parses offsetAngle
 * as a numeric bearing (degrees). The UI uses semantic strings ('upwind',
 * 'downwind') for convenience, but those throw a TypeError when the app
 * casts them to `num?`, silently dropping the entire card from the digital
 * course list. Semantic strings are stripped here so the app falls back to
 * a computed bearing instead of crashing.
 */
function normalizeOffsetAngle(val) {
  if (val == null || val === '') return null;
  if (typeof val === 'number') return Number.isFinite(val) ? val : null;
  if (typeof val === 'string') {
    if (val === 'upwind' || val === 'downwind') return null;
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Produce a line config (start or finish) that is safe for the shipped app.
 * - For 'fixed': emit only markA/markB; offset fields are irrelevant.
 * - For 'vessel': emit only offsetNm/offsetAngle (numeric); marks are N/A.
 * - For 'fixed_mark': a line that sits ON a single fixed mark (markA), oriented
 *   perpendicular to offsetAngle (a numeric bearing — auto-derived per course at
 *   build time, see buildBundleData) and offset offsetNm from the mark (0 = at
 *   the mark). Keeps markA + offsetNm + offsetAngle together; markB is N/A.
 * - For null type (inherited): emit all-null so it falls back to bundle defaults.
 */
function serializeLineConfig(type, markA, markB, offsetNm, offsetAngle) {
  const t = normalizeLineType(type);
  if (t === 'fixed') {
    return {
      type: 'fixed',
      markA: markA || null,
      markB: markB || null,
      offsetNm: null,
      offsetAngle: null,
    };
  }
  if (t === 'vessel') {
    return {
      type: 'vessel',
      markA: null,
      markB: null,
      offsetNm: (typeof offsetNm === 'number' && Number.isFinite(offsetNm)) ? offsetNm : null,
      offsetAngle: normalizeOffsetAngle(offsetAngle),
    };
  }
  if (t === 'fixed_mark') {
    return {
      type: 'fixed_mark',
      markA: markA || null,
      markB: null,
      offsetNm: (typeof offsetNm === 'number' && Number.isFinite(offsetNm)) ? offsetNm : null,
      offsetAngle: normalizeOffsetAngle(offsetAngle),
    };
  }
  return { type: null, markA: null, markB: null, offsetNm: null, offsetAngle: null };
}

/**
 * Bake the auto-derived orientation bearing into a 'fixed_mark' line at publish
 * time. When the user leaves the angle on "auto" (offsetAngle null after
 * normalization), the shipped bundle would otherwise carry no bearing and every
 * surface would have to re-derive it. We resolve it once here — per course, from
 * that course's own mark order — so the published config is deterministic. A
 * user-set numeric bearing is left untouched. Non-fixed_mark lines pass through.
 * (ra-gbrd.22)
 */
function bakeFixedMarkLineAngle(line, markIds, markLookup, isStart) {
  if (!line || line.type !== 'fixed_mark' || line.offsetAngle != null) return line;
  const baked = fixedMarkLineBearing(markIds, markLookup, line.markA, isStart);
  return baked == null ? line : { ...line, offsetAngle: Math.round(baked) };
}

/** Resolve all start/finish fields for a card from bundle defaults. */
function resolveCardLine(card, bd) {
  return {
    startType: normalizeLineType(card.startType ?? bd.defaultStartType),
    startMarkA: card.startMarkA ?? bd.defaultStartMarkA,
    startMarkB: card.startMarkB ?? bd.defaultStartMarkB,
    startOffsetNm: card.startOffsetNm ?? bd.defaultStartOffsetNm,
    startOffsetAngle: card.startOffsetAngle ?? bd.defaultStartOffsetAngle ?? 'downwind',
    finishType: normalizeLineType(card.finishType ?? bd.defaultFinishType),
    finishMarkA: card.finishMarkA ?? bd.defaultFinishMarkA,
    finishMarkB: card.finishMarkB ?? bd.defaultFinishMarkB,
    finishOffsetNm: card.finishOffsetNm ?? bd.defaultFinishOffsetNm,
    finishOffsetAngle: card.finishOffsetAngle ?? bd.defaultFinishOffsetAngle ?? 'upwind',
  };
}

/** Editable start/finish line row (used for bundle defaults). */
function LineConfigRow({ label, type, markA, markB, offsetNm, onTypeChange, onMarkAChange, onMarkBChange, onOffsetChange, offsetLabel }) {
  // Normalize legacy 'offset' → 'vessel' so the select <value> matches its <option>s.
  // Without this, a bundle loaded with defaultStartType='offset' gets normalized to
  // 'vessel' in state, but the select options only include 'fixed' and 'offset' — the
  // select then shows the first option ("Fixed") while the branch below renders the
  // offset UI, which looks broken.
  const normalizedType = normalizeLineType(type);
  return (
    <div className="cc-line-row">
      <span className="form-label">{label}</span>
      <select value={normalizedType} onChange={(e) => onTypeChange(e.target.value)}>
        <option value="fixed">Fixed</option>
        <option value="fixed_mark">Fixed mark</option>
        <option value="vessel">Vessel</option>
      </select>
      {normalizedType === 'fixed' ? (
        <>
          <input
            type="text"
            value={markA}
            onChange={(e) => onMarkAChange(e.target.value)}
            placeholder="Mark A"
            className="inline-input mark-input"
          />
          <span>&harr;</span>
          <input
            type="text"
            value={markB}
            onChange={(e) => onMarkBChange(e.target.value)}
            placeholder="Mark B"
            className="inline-input mark-input"
          />
        </>
      ) : normalizedType === 'fixed_mark' ? (
        <>
          {/* Line on a single fixed mark; orientation auto-derived per course
              at publish (boats' approach/departure direction). (ra-gbrd.22) */}
          <input
            type="text"
            value={markA}
            onChange={(e) => onMarkAChange(e.target.value)}
            placeholder="Mark"
            className="inline-input mark-input"
          />
          <input
            type="number"
            value={offsetNm}
            onChange={(e) => onOffsetChange(parseFloat(e.target.value) || 0)}
            step="0.1"
            min="0"
            className="inline-input mark-input"
            title="Offset of the line from the mark (0 = on the mark)"
          />
          <span>nm · auto bearing</span>
        </>
      ) : (
        <>
          <input
            type="number"
            value={offsetNm}
            onChange={(e) => onOffsetChange(parseFloat(e.target.value) || 0)}
            step="0.1"
            min="0"
            className="inline-input mark-input"
          />
          <span>{offsetLabel}</span>
        </>
      )}
    </div>
  );
}

/** Extract the 10 default-line fields from a bundle, normalizing type and
 *  falling back to the built-in defaults for any missing field. Used to seed
 *  the live editor AND the committed snapshot that buildBundleData bakes. */
function bundleToLineDefaults(bundle) {
  return {
    defaultStartType: normalizeLineType(bundle.defaultStartType ?? DEFAULT_BUNDLE_DEFAULTS.defaultStartType),
    defaultStartOffsetNm: bundle.defaultStartOffsetNm ?? DEFAULT_BUNDLE_DEFAULTS.defaultStartOffsetNm,
    defaultStartOffsetAngle: bundle.defaultStartOffsetAngle ?? DEFAULT_BUNDLE_DEFAULTS.defaultStartOffsetAngle,
    defaultStartMarkA: bundle.defaultStartMarkA ?? DEFAULT_BUNDLE_DEFAULTS.defaultStartMarkA,
    defaultStartMarkB: bundle.defaultStartMarkB ?? DEFAULT_BUNDLE_DEFAULTS.defaultStartMarkB,
    defaultFinishType: normalizeLineType(bundle.defaultFinishType ?? DEFAULT_BUNDLE_DEFAULTS.defaultFinishType),
    defaultFinishOffsetNm: bundle.defaultFinishOffsetNm ?? DEFAULT_BUNDLE_DEFAULTS.defaultFinishOffsetNm,
    defaultFinishOffsetAngle: bundle.defaultFinishOffsetAngle ?? DEFAULT_BUNDLE_DEFAULTS.defaultFinishOffsetAngle,
    defaultFinishMarkA: bundle.defaultFinishMarkA ?? DEFAULT_BUNDLE_DEFAULTS.defaultFinishMarkA,
    defaultFinishMarkB: bundle.defaultFinishMarkB ?? DEFAULT_BUNDLE_DEFAULTS.defaultFinishMarkB,
  };
}

/** Human-readable summary of a start/finish line config, e.g. "Vessel + 0.5nm
 *  downwind", "Fixed: HUT ↔ O", "Fixed mark: X + 0.2nm". Used to REPEAT the
 *  inherited club default inline so the user doesn't have to go find it. (ra-npo7 #3) */
function describeLineConfig(type, markA, markB, offsetNm, offsetLabel) {
  const t = normalizeLineType(type);
  if (t === 'fixed') return `Fixed: ${markA || '—'} ↔ ${markB || '—'}`;
  if (t === 'fixed_mark') return `Fixed mark: ${markA || '—'}${offsetNm ? ` + ${offsetNm}nm` : ''}`;
  return `Vessel${offsetNm ? ` + ${offsetNm}${offsetLabel}` : ''}`;
}

/** Card-level start/finish row — edits bundle default when inherited, card override when overridden. */
function CardLineOverrideRow({
  label, ownType, ownMarkA, ownMarkB, ownOffsetNm, ownOffsetAngle,
  inheritedType, inheritedMarkA, inheritedMarkB, inheritedOffsetNm, inheritedOffsetAngle,
  onClear, fieldPrefix, updateCard, onDefaultChange,
}) {
  const isInherited = ownType == null;
  const effectiveType = normalizeLineType(isInherited ? inheritedType : ownType);
  const effectiveMarkA = isInherited ? inheritedMarkA : ownMarkA;
  const effectiveMarkB = isInherited ? inheritedMarkB : ownMarkB;
  const effectiveOffsetNm = isInherited ? inheritedOffsetNm : (ownOffsetNm ?? inheritedOffsetNm);
  const defaultAngle = fieldPrefix === 'start' ? 'downwind' : 'upwind';
  const effectiveAngle = isInherited
    ? (inheritedOffsetAngle ?? defaultAngle)
    : (ownOffsetAngle ?? inheritedOffsetAngle ?? defaultAngle);
  const onChange = isInherited ? onDefaultChange : updateCard;
  const isCustomBearing = effectiveAngle !== 'upwind' && effectiveAngle !== 'downwind';

  return (
    <div className={`cc-line-row ${isInherited ? 'cc-default' : ''}`}>
      <span className="form-label">{label}</span>
      <select value={effectiveType} onChange={(e) => onChange({ [`${fieldPrefix}Type`]: e.target.value })}>
        <option value="fixed">Fixed</option>
        <option value="fixed_mark">Fixed mark</option>
        <option value="vessel">Vessel</option>
      </select>
      {effectiveType === 'fixed' ? (
        <>
          <input
            type="text"
            value={effectiveMarkA || ''}
            onChange={(e) => onChange({ [`${fieldPrefix}MarkA`]: e.target.value })}
            placeholder="Mark A"
            className="inline-input mark-input"
          />
          <span>&harr;</span>
          <input
            type="text"
            value={effectiveMarkB || ''}
            onChange={(e) => onChange({ [`${fieldPrefix}MarkB`]: e.target.value })}
            placeholder="Mark B"
            className="inline-input mark-input"
          />
        </>
      ) : effectiveType === 'fixed_mark' ? (
        <>
          {/* Line on a single fixed mark (markA); orientation auto-derived per
              course at publish, or pinned to a custom bearing. (ra-gbrd.22) */}
          <input
            type="text"
            value={effectiveMarkA || ''}
            onChange={(e) => onChange({ [`${fieldPrefix}MarkA`]: e.target.value })}
            placeholder="Mark"
            className="inline-input mark-input"
          />
          <input
            type="number"
            value={effectiveOffsetNm ?? 0}
            onChange={(e) => onChange({ [`${fieldPrefix}OffsetNm`]: parseFloat(e.target.value) || 0 })}
            step="0.1"
            min="0"
            className="inline-input mark-input"
            title="Offset of the line from the mark (0 = on the mark)"
          />
          <span>nm</span>
          {(() => {
            const fmAngle = isInherited
              ? inheritedOffsetAngle
              : (ownOffsetAngle ?? inheritedOffsetAngle);
            const fmIsAuto = fmAngle == null || fmAngle === '' || !Number.isFinite(Number(fmAngle));
            return (
              <>
                <select
                  value={fmIsAuto ? 'auto' : 'custom'}
                  onChange={(e) => onChange({ [`${fieldPrefix}OffsetAngle`]: e.target.value === 'auto' ? 'auto' : '0' })}
                  className="inline-input"
                >
                  <option value="auto">auto</option>
                  <option value="custom">bearing...</option>
                </select>
                {!fmIsAuto && (
                  <input
                    type="number"
                    value={fmAngle}
                    onChange={(e) => onChange({ [`${fieldPrefix}OffsetAngle`]: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        const cur = Number(fmAngle);
                        const base = Number.isFinite(cur) ? cur : 0;
                        const next = (((base + (e.key === 'ArrowUp' ? 1 : -1)) % 360) + 360) % 360;
                        onChange({ [`${fieldPrefix}OffsetAngle`]: String(next) });
                      }
                    }}
                    step="1"
                    className="inline-input mark-input"
                    title="Line orientation bearing (axis the line crosses; loops 0–359)"
                    style={{ width: '4em' }}
                  />
                )}
              </>
            );
          })()}
        </>
      ) : (
        <>
          <input
            type="number"
            value={effectiveOffsetNm}
            onChange={(e) => onChange({ [`${fieldPrefix}OffsetNm`]: parseFloat(e.target.value) || 0 })}
            step="0.1"
            min="0"
            className="inline-input mark-input"
            title="Distance from mark to vessel"
          />
          <span>nm</span>
          <select
            value={isCustomBearing ? 'custom' : effectiveAngle}
            onChange={(e) => {
              const v = e.target.value;
              onChange({ [`${fieldPrefix}OffsetAngle`]: v === 'custom' ? '0' : v });
            }}
            className="inline-input"
          >
            <option value="downwind">downwind</option>
            <option value="upwind">upwind</option>
            <option value="custom">bearing...</option>
          </select>
          {isCustomBearing && (
            <input
              type="number"
              value={effectiveAngle}
              onChange={(e) => onChange({ [`${fieldPrefix}OffsetAngle`]: e.target.value })}
              onKeyDown={(e) => {
                // Arrow keys step the bearing and LOOP (0 ↓ → 359, 359 ↑ → 0).
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  const cur = Number(effectiveAngle);
                  const base = Number.isFinite(cur) ? cur : 0;
                  const next = (((base + (e.key === 'ArrowUp' ? 1 : -1)) % 360) + 360) % 360;
                  onChange({ [`${fieldPrefix}OffsetAngle`]: String(next) });
                }
              }}
              step="1"
              className="inline-input mark-input"
              title="Absolute bearing from mark to vessel (loops 0–359)"
              style={{ width: '4em' }}
            />
          )}
        </>
      )}
      {isInherited ? (
        <button
          type="button"
          className="btn btn-xs"
          onClick={() => updateCard({
            [`${fieldPrefix}Type`]: effectiveType,
            [`${fieldPrefix}MarkA`]: inheritedMarkA || '',
            [`${fieldPrefix}MarkB`]: inheritedMarkB || '',
            [`${fieldPrefix}OffsetNm`]: inheritedOffsetNm,
            [`${fieldPrefix}OffsetAngle`]: effectiveAngle,
          })}
          title="Set a different value for this card only"
        >
          Override
        </button>
      ) : (
        <button type="button" className="btn btn-xs" onClick={onClear} title="Use default">
          &times;
        </button>
      )}
    </div>
  );
}

export default function CourseCards() {
  const { clubId } = useParams();

  // Data from Firebase
  const [marks, setMarks] = useState([]);
  const clubMarksRef = useRef([]);
  const [events, setEvents] = useState([]);
  const [bundleId, setBundleId] = useState(null);
  // Per-SEASON effective default line (bundle-or-club baseline) — drives the card
  // preview, the inherited-line display, and what a season bakes.
  const [bundleDefaults, setBundleDefaults] = useState({ ...DEFAULT_BUNDLE_DEFAULTS });
  // The CLUB-WIDE default line edited in Club Settings — kept SEPARATE from
  // bundleDefaults so a season's own baked defaults can never bleed into the club
  // doc via "Save default start/finish". (codex; ra-npo7 #4)
  const [clubDefaults, setClubDefaults] = useState({ ...DEFAULT_BUNDLE_DEFAULTS });
  // Club-wide default line, loaded once (ra-npo7 #4). It's the baseline a bundle
  // without its own baked defaults falls back to (via blankLineDefaults below).
  const clubLineDefaultsRef = useRef(null);
  // The COMMITTED default line for the current season context — what a season
  // bundle bakes + resolves distances from at save/publish. It is snapshotted at
  // load (bundle-explicit → the bundle's defaults; otherwise → the club doc) and
  // updated ONLY by "Save default start/finish". Live edits in Club Settings
  // update bundleDefaults (preview) but NOT this, so unsaved edits never bake or
  // shift saved distances. null = bake no defaults (app uses its built-in). (ra-npo7 #4)
  const committedDefaultsRef = useRef(null);
  // Whether the current season is using the CLUB baseline (true) vs its own baked
  // defaults (false). A club-default save only rebases the season when it's on
  // the club fallback — a season with its own defaults keeps them. (codex)
  const seasonUsesClubFallbackRef = useRef(true);
  const blankLineDefaults = useCallback(
    () => (clubLineDefaultsRef.current
      ? { ...DEFAULT_BUNDLE_DEFAULTS, ...clubLineDefaultsRef.current }
      : { ...DEFAULT_BUNDLE_DEFAULTS }),
    [],
  );
  // Seed the committed snapshot when a season loads with no explicit defaults:
  // the club baseline (or null when the club has none).
  const seedCommittedFromClub = useCallback(() => {
    committedDefaultsRef.current = clubLineDefaultsRef.current;
    seasonUsesClubFallbackRef.current = true;
  }, []);

  // Editable state
  const [courseCards, setCourseCards] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');

  // UI state
  const [selectedCardIdx, setSelectedCardIdx] = useState(0);
  const [selectedCell, setSelectedCell] = useState(null);
  const [showPasteImport, setShowPasteImport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showCardImport, setShowCardImport] = useState(false);
  const [importSourceEvent, setImportSourceEvent] = useState('');
  const [importSourceCards, setImportSourceCards] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [showMarkImport, setShowMarkImport] = useState(false);
  const [markImportSourceEvent, setMarkImportSourceEvent] = useState('');
  const [markImportSourceMarks, setMarkImportSourceMarks] = useState(null);
  const [markImportLoading, setMarkImportLoading] = useState(false);
  const [wipExists, setWipExists] = useState(false);
  const [showDiffDialog, setShowDiffDialog] = useState(false);
  const [diffData, setDiffData] = useState(null);

  const [fleetClasses, setFleetClasses] = useState([]);

  // Per-club course-grid config (16/8/4 directions, name scheme, numbering).
  // Loaded once per club; not season-specific. Defaults to the DBSC 16×8 grid.
  const [gridConfig, setGridConfig] = useState({ ...DEFAULT_GRID_CONFIG });
  const [gridConfigSaving, setGridConfigSaving] = useState(false);

  // Club-wide settings common home (ra-npo7): one panel for the course grid +
  // the default start/finish line, instead of those surfaces living apart in the
  // left column and the card-settings block.
  const [showClubSettings, setShowClubSettings] = useState(false);
  // Independent save of the club-wide default start/finish line (ra-npo7 #4).
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [defaultsSaved, setDefaultsSaved] = useState(false);
  // The card-settings block, scrolled into view by the pinned header's Edit btn.
  const cardSettingsRef = useRef(null);
  const scrollToCardSettings = useCallback(() => {
    cardSettingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Editing the club-wide default line updates the CLUB editor state + marks the
  // INDEPENDENT "Save default start/finish" button dirty. It deliberately does
  // NOT touch bundleDefaults (the season's effective line), the season draft, or
  // the committed snapshot — the club default is saved to the club doc on its
  // own, and only the COMMITTED default bakes into a bundle. (Chris ra-npo7 #4; codex)
  const updateDefault = useCallback((patch) => {
    setClubDefaults((d) => ({ ...d, ...patch }));
    setDefaultsSaved(false);
  }, []);

  const markLookup = useMemo(() => buildMarkLookup(marks), [marks]);
  const markImportSummary = useMemo(() => {
    if (!markImportSourceMarks || markImportSourceMarks.length === 0) return null;
    const currentIds = new Set(marks.map((m) => m.id));
    const newCount = markImportSourceMarks.filter((m) => !currentIds.has(m.id)).length;
    return { total: markImportSourceMarks.length, newCount, existingCount: markImportSourceMarks.length - newCount };
  }, [markImportSourceMarks, marks]);

  // Load initial data — default to latest season
  useEffect(() => {
    async function load() {
      try {
        const [clubMarks, clubEvents, fleetConfig, courseGridConfig, clubLineDefaults] = await Promise.all([
          getClubMarks(clubId),
          getClubEvents(clubId),
          loadFleetConfig(clubId),
          loadCourseGridConfig(clubId),
          loadClubLineDefaults(clubId),
        ]);
        clubMarksRef.current = clubMarks;
        setFleetClasses(fleetConfig.classes || []);
        setGridConfig(courseGridConfig);
        setEvents(clubEvents);
        // Club-wide default start/finish is the baseline (ra-npo7 #4). A season
        // bundle with its own baked defaults still overrides below.
        clubLineDefaultsRef.current = clubLineDefaults;
        // Seed the committed snapshot from the club baseline. A bundle with its
        // OWN baked defaults overrides it below; a bundle without keeps it, so
        // the first save/publish still bakes the displayed club default. (codex)
        committedDefaultsRef.current = clubLineDefaults;
        seasonUsesClubFallbackRef.current = true;
        // The Club Settings editor always shows the CLUB-WIDE default (or the
        // built-in default when the club has none), independent of the season.
        setClubDefaults(clubLineDefaults
          ? { ...DEFAULT_BUNDLE_DEFAULTS, ...clubLineDefaults }
          : { ...DEFAULT_BUNDLE_DEFAULTS });
        if (clubLineDefaults) {
          setBundleDefaults((d) => ({ ...d, ...clubLineDefaults }));
        }

        // Default to latest season
        const sorted = [...clubEvents].sort((a, b) => ((b.date || b.id) > (a.date || a.id) ? 1 : -1));
        const latestEventId = sorted[0]?.id || '';
        setSelectedEventId(latestEventId);

        if (latestEventId) {
          // Try WIP first, then latest published
          let bundle = await loadWip(clubId, latestEventId);
          let isWip = !!bundle;
          if (!bundle) {
            const published = await getEventPublishedBundles(clubId, latestEventId);
            bundle = published[0] ?? null; // sorted by version desc
          }
          if (bundle) {
            setBundleId(bundle.id);
            setWipExists(isWip);
            const merged = mergeMarks(clubMarks, bundle.marks);
            setMarks(merged);
            if (bundle.defaultStartType !== undefined) {
              // Season bundle carries its OWN baked defaults — they win over the
              // club baseline for this season. Snapshot them as committed.
              const bd = bundleToLineDefaults(bundle);
              setBundleDefaults(bd);
              committedDefaultsRef.current = pickLineDefaults(bd); seasonUsesClubFallbackRef.current = false;
            }
            const cards = (bundle.course_cards || []).map((c) => ({
              ...c,
              id: c.id || crypto.randomUUID(),
              courses: c.courses || [],
              sailing_classes: c.sailing_classes || [],
              properties: Array.isArray(c.properties) ? c.properties : [],
              startType: c.startType !== undefined ? c.startType : 'fixed',
              finishType: c.finishType !== undefined ? c.finishType : 'fixed',
            }));
            setCourseCards(cards.length > 0 ? cards : []);
          } else {
            setMarks(clubMarks);
          }
        } else {
          setMarks(clubMarks);
        }
      } catch (err) {
        setError('Failed to load data: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [clubId]);

  // Reload data when season changes (skip initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    async function loadSeason() {
      setLoading(true);
      setError('');
      setSaved(false);
      setWipExists(false);
      setSelectedCardIdx(0);
      setSelectedCell(null);
      try {
        if (!selectedEventId) {
          // "New Season" — start fresh
          setBundleId(null);
          setCourseCards([]);
          setBundleDefaults(blankLineDefaults()); seedCommittedFromClub();
          setMarks(clubMarksRef.current);
        } else {
          // Try WIP first, then latest published
          let bundle = await loadWip(clubId, selectedEventId);
          let isWip = !!bundle;
          if (!bundle) {
            const published = await getEventPublishedBundles(clubId, selectedEventId);
            bundle = published[0] ?? null;
          }
          if (bundle) {
            setBundleId(bundle.id);
            setWipExists(isWip);
            const merged = mergeMarks(clubMarksRef.current, bundle.marks);
            setMarks(merged);
            if (bundle.defaultStartType !== undefined) {
              // Season bundle carries its OWN baked defaults — they win over the
              // club baseline for this season. Snapshot them as committed.
              const bd = bundleToLineDefaults(bundle);
              setBundleDefaults(bd);
              committedDefaultsRef.current = pickLineDefaults(bd); seasonUsesClubFallbackRef.current = false;
            } else {
              setBundleDefaults(blankLineDefaults()); seedCommittedFromClub();
            }
            const cards = (bundle.course_cards || []).map((c) => ({
              ...c,
              id: c.id || crypto.randomUUID(),
              courses: c.courses || [],
              sailing_classes: c.sailing_classes || [],
              properties: Array.isArray(c.properties) ? c.properties : [],
              startType: c.startType !== undefined ? c.startType : 'fixed',
              finishType: c.finishType !== undefined ? c.finishType : 'fixed',
            }));
            setCourseCards(cards);
          } else {
            setBundleId(null);
            setCourseCards([]);
            setBundleDefaults(blankLineDefaults()); seedCommittedFromClub();
            setMarks(clubMarksRef.current);
          }
        }
      } catch (err) {
        setError('Failed to load season: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    loadSeason();
  }, [selectedEventId, clubId]);

  // Named course selection (alongside grid cell selection)
  const [selectedNamedCourseId, setSelectedNamedCourseId] = useState(null);

  // Derived state
  const selectedCard = courseCards[selectedCardIdx] || null;

  // Separate grid courses from named courses
  const gridCourses = useMemo(() =>
    (selectedCard?.courses || []).filter((c) => c.directionCode != null),
    [selectedCard],
  );
  const namedCourses = useMemo(() =>
    (selectedCard?.courses || []).filter((c) => c.directionCode == null),
    [selectedCard],
  );
  const hasGridCourses = gridCourses.length > 0;
  const hasNamedCourses = namedCourses.length > 0;

  const selectedCourse = useMemo(() => {
    if (!selectedCard) return null;
    // Named course selected
    if (selectedNamedCourseId) {
      return selectedCard.courses.find((c) => c.id === selectedNamedCourseId) || null;
    }
    // Grid cell selected
    if (!selectedCell) return null;
    return selectedCard.courses.find(
      (c) => c.directionCode === selectedCell.dir &&
             c.courseNumber === selectedCell.num,
    ) || newGridCourse(selectedCell.dir, selectedCell.num, gridConfig);
  }, [selectedCard, selectedCell, selectedNamedCourseId, gridConfig]);

  // Resolve start/finish for preview: course → card → bundle. mergePreviewStartFinish
  // carries every overridable field (incl. the offset ANGLE) so a configured
  // bearing actually reaches the chart. (ra-etwi)
  const previewStartFinish = useMemo(() => {
    if (!selectedCard) return null;
    const cardLine = resolveCardLine(selectedCard, bundleDefaults);
    return mergePreviewStartFinish(selectedCourse, cardLine);
  }, [selectedCard, selectedCourse, bundleDefaults]);

  // Prev/next grid cell within the configured row. Derived from gridConfig so
  // editor navigation lands on real cells under any shape (gapped numbers,
  // narrower rows) — not a hardcoded ±1 / bound-8. (ra-gbrd.20)
  const gridNav = useMemo(() => {
    if (!selectedCell) return { prev: null, next: null };
    const rowIdx = directionLettersFor(gridConfig.directionCount).indexOf(selectedCell.dir);
    if (rowIdx < 0) return { prev: null, next: null };
    const nums = courseNumbersFor(gridConfig, rowIdx);
    const col = nums.indexOf(selectedCell.num);
    if (col < 0) return { prev: null, next: null };
    return {
      prev: col > 0 ? nums[col - 1] : null,
      next: col < nums.length - 1 ? nums[col + 1] : null,
    };
  }, [selectedCell, gridConfig]);

  // Find adjacent (previous) course for variation pre-population (e.g. D4 → D5).
  const adjacentCourse = useMemo(() => {
    if (!selectedCard || gridNav.prev == null) return null;
    return selectedCard.courses.find(
      (c) => c.directionCode === selectedCell.dir &&
             c.courseNumber === gridNav.prev,
    ) || null;
  }, [selectedCard, selectedCell, gridNav]);

  // A card's course selection is never empty while the card has a course to
  // show (ra-h29bt). Selecting a card used to clear both selections and leave
  // the editor on "Select a cell in the grid" — on an ISORA card carrying its
  // one course that is a dead tap, and on any card it is a step with a single
  // sensible answer. Every entry into a card runs through here: the card tabs,
  // first load, a season change, adding or removing a card, and a grid-config
  // change that moves which cells exist.
  //
  // This is the general >=1 rule, not the narrow single-course one, and it is
  // safe HERE specifically because selection on this screen is pure UI state:
  // handleSelectCell / handleSelectNamedCourse set state and nothing else, no
  // write, no dirty flag, and both grids stay on screen beside the editor so
  // there is no level to navigate back to. The portal's other empty selections
  // are load-bearing (a filter's "All", a field's unset, a gate holding a
  // publish or a fleet broadcast disabled) and are deliberately untouched.
  //
  // useLayoutEffect, not useEffect: a plain effect runs after paint, so a card
  // switch would show one frame of the empty editor before the course appears.
  //
  // "Nothing selected" is judged on the RESOLVED selection, not on the raw ids.
  // Two ways a raw id lies: handleAddCard changes card without clearing
  // selectedNamedCourseId, so a stale id from the previous card reads as a
  // selection but resolves to no course; and a selected CELL can fall off the
  // grid without moving, because the club's grid settings are live on this page
  // — shrink "Courses / direction" and the editor would otherwise keep editing a
  // course the grid no longer draws. Both are treated as nothing selected, which
  // is what makes the out-of-grid rule an invariant rather than a rule about
  // where a selection may START. (codex on 2bcb16c)
  useLayoutEffect(() => {
    if (!selectedCard) return;
    if (cellIsRendered(selectedCell, gridConfig)) return;
    if (selectedCell == null && selectedNamedCourseId != null &&
        selectedCard.courses.some((c) => c.id === selectedNamedCourseId)) return;

    const pick = firstCourseSelection(selectedCard, gridConfig);
    if (!pick) {
      // Nothing selectable on this card — drop a stale selection so the empty
      // state is reporting this card and this grid, not the last one.
      if (selectedNamedCourseId != null) setSelectedNamedCourseId(null);
      if (selectedCell != null) setSelectedCell(null);
      return;
    }
    setSelectedNamedCourseId(pick.namedCourseId);
    setSelectedCell(pick.cell);
  }, [selectedCard, selectedCell, selectedNamedCourseId, gridConfig]);

  // Card operations
  const handleAddCard = useCallback(() => {
    setCourseCards((prev) => [...prev, newCard(`Card ${prev.length + 1}`)]);
    setSelectedCardIdx(courseCards.length);
    setSelectedCell(null);
    setSaved(false);
  }, [courseCards.length]);

  const handleRemoveCard = useCallback((idx) => {
    setCourseCards((prev) => prev.filter((_, i) => i !== idx));
    if (selectedCardIdx >= courseCards.length - 1) {
      setSelectedCardIdx(Math.max(0, courseCards.length - 2));
    }
    setSelectedCell(null);
    setSaved(false);
  }, [selectedCardIdx, courseCards.length]);

  const updateCard = useCallback((idx, updates) => {
    setCourseCards((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, ...updates } : c)),
    );
    setSaved(false);
  }, []);

  // Course operations
  const handleSelectCell = useCallback((dir, num) => {
    setSelectedCell({ dir, num });
    setSelectedNamedCourseId(null);
  }, []);

  // Update one field of the grid config in local state (UI only until saved).
  const updateGridConfig = useCallback((patch) => {
    setGridConfig((prev) => {
      try {
        return buildGridConfig({ ...prev, ...patch });
      } catch {
        return prev; // ignore an out-of-range value
      }
    });
  }, []);

  const handleSaveGridConfig = useCallback(async () => {
    setGridConfigSaving(true);
    setError('');
    try {
      await saveCourseGridConfig(clubId, gridConfig);
    } catch (err) {
      setError('Grid config save failed: ' + (err.message || err));
    } finally {
      setGridConfigSaving(false);
    }
  }, [clubId, gridConfig]);

  // Independent save of the club-wide default start/finish line (ra-npo7 #4) —
  // persists to the club doc, decoupled from the season-draft "Save draft".
  const handleSaveDefaults = useCallback(async () => {
    setSavingDefaults(true);
    setError('');
    try {
      await saveClubLineDefaults(clubId, clubDefaults);
      // Keep both refs current: the club baseline (season fallback) AND the
      // committed snapshot (what THIS season now bakes). (codex findings 1 + 2)
      const saved = pickLineDefaults(clubDefaults);
      clubLineDefaultsRef.current = saved;
      // Only rebase the CURRENT season onto the new club default when that season
      // is using the club fallback. A season with its own baked defaults keeps
      // them. (codex)
      if (seasonUsesClubFallbackRef.current) {
        committedDefaultsRef.current = saved;
        setBundleDefaults((d) => ({ ...d, ...saved }));
      }
      setDefaultsSaved(true);
    } catch (err) {
      setError('Default start/finish save failed: ' + (err.message || err));
    } finally {
      setSavingDefaults(false);
    }
  }, [clubId, clubDefaults]);

  const handleCourseChange = useCallback((updatedCourse) => {
    if (!selectedCard) return;
    setCourseCards((prev) =>
      prev.map((card, i) => {
        if (i !== selectedCardIdx) return card;
        let existing;
        if (updatedCourse.directionCode == null) {
          // Named course — find by ID
          existing = card.courses.findIndex((c) => c.id === updatedCourse.id);
        } else {
          // Grid course — find by direction+number
          existing = card.courses.findIndex(
            (c) => c.directionCode === updatedCourse.directionCode &&
                   c.courseNumber === updatedCourse.courseNumber,
          );
        }
        const courses = [...card.courses];
        if (existing >= 0) {
          courses[existing] = updatedCourse;
        } else {
          courses.push(updatedCourse);
        }
        return { ...card, courses };
      }),
    );
    setSaved(false);
  }, [selectedCardIdx, selectedCard]);

  const handleNavigate = useCallback((dir, num) => {
    setSelectedCell({ dir, num });
  }, []);

  // Named course operations
  const handleAddNamedCourse = useCallback(() => {
    const cardIdx = selectedCardIdx;
    const card = courseCards[cardIdx];
    const courseCount = card ? card.courses.length : 0;
    const defaultName = `Course ${courseCount + 1}`;
    const course = newNamedCourse(defaultName);
    if (card) {
      // Add to current card
      setCourseCards((prev) =>
        prev.map((c, i) => i === cardIdx ? { ...c, courses: [...c.courses, course] } : c),
      );
    } else {
      // No card exists — create one
      const newC = newCard('');
      newC.courses = [course];
      setCourseCards((prev) => [...prev, newC]);
      setSelectedCardIdx(courseCards.length);
    }
    setSelectedCell(null);
    setSelectedNamedCourseId(course.id);
    setShowAddPanel(false);
    setSaved(false);
  }, [courseCards, selectedCardIdx]);

  const handleSelectNamedCourse = useCallback((courseId) => {
    setSelectedNamedCourseId(courseId);
    setSelectedCell(null);
  }, []);

  const handleRemoveNamedCourse = useCallback((courseId) => {
    if (!selectedCard) return;
    setCourseCards((prev) =>
      prev.map((card, i) => {
        if (i !== selectedCardIdx) return card;
        return { ...card, courses: card.courses.filter((c) => c.id !== courseId) };
      }),
    );
    if (selectedNamedCourseId === courseId) setSelectedNamedCourseId(null);
    setSaved(false);
  }, [selectedCardIdx, selectedCard, selectedNamedCourseId]);

  // Import card(s) from another season
  async function loadImportSource(eventId) {
    if (!eventId) {
      setImportSourceCards(null);
      return;
    }
    setImportLoading(true);
    setError('');
    try {
      const bundle = await getEventBundle(clubId, eventId);
      if (!bundle || !bundle.course_cards?.length) {
        setImportSourceCards([]);
        return;
      }
      // Merge marks from source bundle so imported courses can resolve their marks
      if (bundle.marks?.length) {
        const merged = mergeMarks(clubMarksRef.current, bundle.marks);
        clubMarksRef.current = merged;
        setMarks(merged);
      }
      setImportSourceCards(bundle.course_cards);
    } catch (err) {
      setError('Failed to load season: ' + err.message);
      setImportSourceCards(null);
    } finally {
      setImportLoading(false);
    }
  }

  function handleImportCard(card) {
    const imported = {
      ...card,
      id: crypto.randomUUID(),
      courses: (card.courses || []).map((c) => ({
        ...c,
        id: crypto.randomUUID(),
      })),
      sailing_classes: card.sailing_classes || [],
    };
    setCourseCards((prev) => [...prev, imported]);
    setSelectedCardIdx(courseCards.length);
    setSelectedCell(null);
    setSaved(false);
    setShowCardImport(false);
    setImportSourceEvent('');
    setImportSourceCards(null);
  }

  function handleImportAllCards(cards) {
    const imported = cards.map((card) => ({
      ...card,
      id: crypto.randomUUID(),
      courses: (card.courses || []).map((c) => ({
        ...c,
        id: crypto.randomUUID(),
      })),
      sailing_classes: card.sailing_classes || [],
    }));
    setCourseCards((prev) => [...prev, ...imported]);
    setSelectedCardIdx(0);
    setSelectedCell(null);
    setSaved(false);
    setShowCardImport(false);
    setImportSourceEvent('');
    setImportSourceCards(null);
  }

  // Import marks from another season
  async function loadMarkImportSource(eventId) {
    if (!eventId) {
      setMarkImportSourceMarks(null);
      return;
    }
    setMarkImportLoading(true);
    setError('');
    try {
      const bundle = await getEventBundle(clubId, eventId);
      if (!bundle || !bundle.marks?.length) {
        setMarkImportSourceMarks([]);
        return;
      }
      setMarkImportSourceMarks(bundle.marks);
    } catch (err) {
      setError('Failed to load marks: ' + err.message);
      setMarkImportSourceMarks(null);
    } finally {
      setMarkImportLoading(false);
    }
  }

  function handleImportMarks(sourceMarks) {
    const merged = mergeMarks(clubMarksRef.current, sourceMarks);
    clubMarksRef.current = merged;
    setMarks(merged);
    setShowMarkImport(false);
    setMarkImportSourceEvent('');
    setMarkImportSourceMarks(null);
    setSaved(false);
  }

  // Paste import
  const handlePasteImport = useCallback((courses) => {
    if (!selectedCard) return;
    setCourseCards((prev) =>
      prev.map((card, i) => {
        if (i !== selectedCardIdx) return card;
        const existing = new Map(
          card.courses.map((c) => [`${c.directionCode}${c.courseNumber}`, c]),
        );
        for (const c of courses) {
          existing.set(`${c.directionCode}${c.courseNumber}`, {
            ...c,
            id: c.id || crypto.randomUUID(),
          });
        }
        return { ...card, courses: Array.from(existing.values()) };
      }),
    );
    setShowPasteImport(false);
    setSaved(false);
  }, [selectedCardIdx, selectedCard]);

  // Build the bundle data object from current state
  function buildBundleData() {
    // Bake + resolve from the COMMITTED default line, never the live-edited
    // bundleDefaults. So (a) an unsaved Club-Settings edit never leaks into the
    // season draft, (b) a season with its own baked defaults keeps them (they're
    // the committed value, not overwritten by the club baseline), and (c) course
    // distances use the same baseline that gets baked. null committed → bake no
    // defaults; distances resolve from the built-in baseline. (codex findings)
    const committed = committedDefaultsRef.current;
    const resolveBaseline = committed || { ...DEFAULT_BUNDLE_DEFAULTS };
    const bdStart = serializeLineConfig(
      resolveBaseline.defaultStartType,
      resolveBaseline.defaultStartMarkA,
      resolveBaseline.defaultStartMarkB,
      resolveBaseline.defaultStartOffsetNm,
      resolveBaseline.defaultStartOffsetAngle,
    );
    const bdFinish = serializeLineConfig(
      resolveBaseline.defaultFinishType,
      resolveBaseline.defaultFinishMarkA,
      resolveBaseline.defaultFinishMarkB,
      resolveBaseline.defaultFinishOffsetNm,
      resolveBaseline.defaultFinishOffsetAngle,
    );
    const data = {
      club_id: clubId,
      event_id: selectedEventId || clubId,
      ...(committed ? {
        defaultStartType: bdStart.type,
        defaultStartOffsetNm: bdStart.offsetNm,
        defaultStartOffsetAngle: bdStart.offsetAngle,
        defaultStartMarkA: bdStart.markA,
        defaultStartMarkB: bdStart.markB,
        defaultFinishType: bdFinish.type,
        defaultFinishOffsetNm: bdFinish.offsetNm,
        defaultFinishOffsetAngle: bdFinish.offsetAngle,
        defaultFinishMarkA: bdFinish.markA,
        defaultFinishMarkB: bdFinish.markB,
      } : {}),
      course_cards: courseCards.map((card) => {
        // Apply shape-per-type normalization so the shipped app can parse
        // the bundle. Fixed emits only markA/markB; vessel emits only
        // numeric offsetNm/offsetAngle (semantic 'upwind'/'downwind' strings
        // → null since the app parses offsetAngle as num?).
        const cardStart = card.startType
          ? serializeLineConfig(card.startType, card.startMarkA, card.startMarkB, card.startOffsetNm, card.startOffsetAngle)
          : { type: null, markA: null, markB: null, offsetNm: null, offsetAngle: null };
        const cardFinish = card.finishType
          ? serializeLineConfig(card.finishType, card.finishMarkA, card.finishMarkB, card.finishOffsetNm, card.finishOffsetAngle)
          : { type: null, markA: null, markB: null, offsetNm: null, offsetAngle: null };
        return {
          id: card.id,
          name: card.name,
          vhf: card.vhf,
          footnotes: Array.isArray(card.footnotes)
            ? card.footnotes
            : card.footnotes
              ? String(card.footnotes).split('\n').map(s => s.trim()).filter(Boolean)
              : [],
          sailing_classes: card.sailing_classes,
          properties: (card.properties || [])
            .map((p) => ({ key: (p.key || '').trim(), value: (p.value || '').trim() }))
            .filter((p) => p.key || p.value)
            .slice(0, 3),
          startType: cardStart.type,
          startMarkA: cardStart.markA,
          startMarkB: cardStart.markB,
          startOffsetNm: cardStart.offsetNm,
          startOffsetAngle: cardStart.offsetAngle,
          finishType: cardFinish.type,
          finishMarkA: cardFinish.markA,
          finishMarkB: cardFinish.markB,
          finishOffsetNm: cardFinish.offsetNm,
          finishOffsetAngle: cardFinish.offsetAngle,
          courses: card.courses.map((c) => {
            // Compute distance from mark coordinates if not already set.
            // Uses raw resolved values (pre-normalization) so the calc sees
            // the full inheritance chain even for 'fixed' starts.
            const resolvedLine = resolveCardLine(card, resolveBaseline);
            const effectiveLine = {
              startType: c.startType ?? resolvedLine.startType,
              startOffsetNm: c.startOffsetNm ?? resolvedLine.startOffsetNm,
              startMarkA: c.startMarkA ?? resolvedLine.startMarkA,
              startMarkB: c.startMarkB ?? resolvedLine.startMarkB,
              finishType: c.finishType ?? resolvedLine.finishType,
              finishOffsetNm: c.finishOffsetNm ?? resolvedLine.finishOffsetNm,
              finishMarkA: c.finishMarkA ?? resolvedLine.finishMarkA,
              finishMarkB: c.finishMarkB ?? resolvedLine.finishMarkB,
            };
            // Canonicalize markIds to the marks' real case before BOTH distance
            // calc and serialization. Courses corrupted by the old lowercasing
            // bug (ra-or1y) carry e.g. ['n','m','c'] against uppercase marks;
            // publishing without first opening each course in the editor would
            // otherwise miss every markLookup entry (distance 0) AND ship the
            // broken lowercase ids into the bundle. This self-heals them at
            // publish time. (codex review on d7f15131.)
            const canonMarkIds = canonicalizeMarkIds(c.markIds, marks);
            // If canonicalization actually changed an id, the course was corrupted
            // by the old lowercasing bug — which means its STORED distance was also
            // computed against the broken (lookup-missing) ids and is a stale 0.
            // In that case prefer the freshly recomputed distance below; otherwise
            // keep the user's stored distance. (codex review on 601ca925.)
            const rawIds = Array.isArray(c.markIds) ? c.markIds : [];
            const idsHealed = canonMarkIds.length !== rawIds.length ||
              canonMarkIds.some((id, i) => id !== rawIds[i]);
            const distResult = calculateFullDistance(
              canonMarkIds,
              markLookup,
              { type: effectiveLine.startType, offsetNm: effectiveLine.startOffsetNm, markA: effectiveLine.startMarkA, markB: effectiveLine.startMarkB },
              { type: effectiveLine.finishType, offsetNm: effectiveLine.finishOffsetNm, markA: effectiveLine.finishMarkA, markB: effectiveLine.finishMarkB },
            );
            // Resolve markA for the bake when the course inherits the line from
            // its card/bundle default (c.*Type null → use the effective line).
            const cStart = bakeFixedMarkLineAngle(
              c.startType
                ? serializeLineConfig(c.startType, c.startMarkA, c.startMarkB, c.startOffsetNm, c.startOffsetAngle)
                : { type: null, markA: null, markB: null, offsetNm: null, offsetAngle: null },
              canonMarkIds, markLookup, true,
            );
            const cFinish = bakeFixedMarkLineAngle(
              c.finishType
                ? serializeLineConfig(c.finishType, c.finishMarkA, c.finishMarkB, c.finishOffsetNm, c.finishOffsetAngle)
                : { type: null, markA: null, markB: null, offsetNm: null, offsetAngle: null },
              canonMarkIds, markLookup, false,
            );
            return {
              id: c.id,
              // Preserve null for named courses; only grid courses have a letter.
              // (Coercing to 'X' made named courses vanish — they'd no longer
              // match `directionCode == null` on reload, but 'X' also isn't a
              // valid grid row so they wouldn't show in the grid either.)
              directionCode: c.directionCode ?? null,
              courseNumber: c.courseNumber ?? 0,
              name: c.name || undefined,
              // Named-course wind sector (e.g. 'NE') + start area. Grid courses
              // derive direction from directionCode, so these stay null there.
              // The app deserializes windDirection (course_model.dart) and
              // renders it verbatim — no app change required. (ra-gbrd.19)
              windDirection: c.windDirection || null,
              startArea: c.startArea || null,
              markIds: canonMarkIds,
              markDirections: c.markDirections,
              bearing: c.bearing ?? 0,
              distance: idsHealed
                ? (distResult.totalNm ?? c.distance ?? 0)
                : (c.distance ?? distResult.totalNm ?? 0),
              startType: cStart.type,
              startMarkA: cStart.markA,
              startMarkB: cStart.markB,
              startOffsetNm: cStart.offsetNm,
              startOffsetAngle: cStart.offsetAngle,
              finishType: cFinish.type,
              finishMarkA: cFinish.markA,
              finishMarkB: cFinish.markB,
              finishOffsetNm: cFinish.offsetNm,
              finishOffsetAngle: cFinish.offsetAngle,
            };
          }),
        };
      }),
      marks: marks.map((m) => ({
        id: m.id,
        name: m.name || m.id,
        latitude: m.latitude || 0,
        longitude: m.longitude || 0,
        appearance: m.appearance || '',
        // Preserve the mark TYPE so shore stations / committee boats don't
        // collapse to the buoy section in any view that reads the published
        // bundle, and the LIGHTS characteristics so they survive publish.
        // (ra-gbrd.29 — same publish-loses-data class as ra-gbrd.19's
        // windDirection drop.) Default type to 'racing_mark' to match
        // marksStore's canonical mark shape.
        type: m.type || 'racing_mark',
        ...(m.lights ? { lights: m.lights } : {}),
        ...(m.vhf ? { vhf: m.vhf } : {}),
        ...(m.isLive != null ? { isLive: m.isLive } : {}),
      })),
    };
    return data;
  }

  // Save
  async function doSave() {
    const eventId = selectedEventId || clubId;
    await saveWip(clubId, eventId, buildBundleData());
    setWipExists(true);
    setSaved(true);
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await doSave();
    } catch (err) {
      setError('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    setSaving(true);
    setError('');
    try {
      // Save WIP first
      await doSave();
      // Load latest published for diff
      const eventId = selectedEventId || clubId;
      const allPublished = await getEventPublishedBundles(clubId, eventId);
      const published = allPublished[0] ?? null;
      // Build diff
      const wipData = buildBundleData();
      const diff = computeBundleDiff(published, wipData);
      setDiffData(diff);
      setShowDiffDialog(true);
    } catch (err) {
      setError('Bundle creation failed: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  }

  async function handlePublishConfirm() {
    setPublishing(true);
    setShowDiffDialog(false);
    setError('');
    try {
      const eventId = selectedEventId || clubId;
      const result = await publishWip(clubId, eventId);
      setPublishResult(result);
      setWipExists(false);
    } catch (err) {
      setError('Bundle creation failed: ' + (err.message || err));
    } finally {
      setPublishing(false);
    }
  }

  async function handleDiscard() {
    if (!confirm('Discard draft courses? Marks will be preserved.')) return;
    setError('');
    try {
      const eventId = selectedEventId || clubId;
      // Clear only course_cards and defaults from WIP — preserve marks
      await saveWip(clubId, eventId, { course_cards: [], defaultStartType: null, defaultFinishType: null });
      setWipExists(false);
      setSaved(false);
      // Reload from latest published
      const allPublished = await getEventPublishedBundles(clubId, eventId);
      const published = allPublished[0];
      if (published) {
        const merged = mergeMarks(clubMarksRef.current, published.marks);
        setMarks(merged);
        if (published.defaultStartType !== undefined) {
          const pd = bundleToLineDefaults(published);
          setBundleDefaults(pd);
          committedDefaultsRef.current = pickLineDefaults(pd); seasonUsesClubFallbackRef.current = false;
        } else {
          setBundleDefaults(blankLineDefaults()); seedCommittedFromClub();
        }
        const cards = (published.course_cards || []).map((c) => ({
          ...c,
          id: c.id || crypto.randomUUID(),
          courses: c.courses || [],
          sailing_classes: c.sailing_classes || [],
          properties: Array.isArray(c.properties) ? c.properties : [],
          startType: c.startType !== undefined ? c.startType : 'fixed',
          finishType: c.finishType !== undefined ? c.finishType : 'fixed',
        }));
        setCourseCards(cards);
      } else {
        setCourseCards([]);
        setBundleDefaults(blankLineDefaults()); seedCommittedFromClub();
        setMarks(clubMarksRef.current);
      }
    } catch (err) {
      setError('Discard failed: ' + (err.message || err));
    }
  }

  // Sailing class management for selected card
  function toggleSailingClass(cls) {
    if (!selectedCard) return;
    const existing = selectedCard.sailing_classes || [];
    const idx = existing.findIndex((sc) => sc.name === cls.name);
    if (idx >= 0) {
      // Remove
      updateCard(selectedCardIdx, { sailing_classes: existing.filter((_, i) => i !== idx) });
    } else {
      // Add with defaults
      updateCard(selectedCardIdx, {
        sailing_classes: [...existing, { name: cls.name, flag: cls.flag || '', startTime: '' }],
      });
    }
  }

  function updateSailingClass(classIdx, field, value) {
    if (!selectedCard) return;
    const classes = selectedCard.sailing_classes.map((sc, i) =>
      i === classIdx ? { ...sc, [field]: value } : sc,
    );
    updateCard(selectedCardIdx, { sailing_classes: classes });
  }

  function addCustomSailingClass(name) {
    if (!selectedCard || !name.trim()) return;
    const existing = selectedCard.sailing_classes || [];
    if (existing.some((sc) => sc.name === name.trim())) return;
    updateCard(selectedCardIdx, {
      sailing_classes: [...existing, { name: name.trim(), flag: '', startTime: '' }],
    });
  }

  if (loading) {
    return <div className="page"><p>Loading...</p></div>;
  }

  return (
    <>
      <PortalHeader />
      <div className="course-cards-page">
        {/* Top bar */}
        <header className="cc-header">
        <div className="cc-header-left">
          <Link to={`/clubs/${clubId}`} className="back-link">&larr; Dashboard</Link>
          <h1>Course Cards</h1>
        </div>
        <div className="cc-header-right">
          <label className="form-field-inline">
            <span className="form-label">Season:</span>
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              <option value="">New Season</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.id}</option>
              ))}
            </select>
          </label>
          <button
            className="btn btn-sm"
            onClick={() => setShowClubSettings((v) => !v)}
            title="Club-wide settings: course grid + default start/finish line"
          >
            {showClubSettings ? 'Hide club settings' : '⚙ Club settings'}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={saving || courseCards.length === 0}
          >
            {saving ? 'Saving...' : saved ? 'Saved' : 'Save draft'}
          </button>
          {wipExists && (
            <button
              className="btn btn-sm btn-danger"
              onClick={handleDiscard}
              disabled={saving || publishing}
            >
              Discard draft
            </button>
          )}
          <button
            className="btn btn-sm"
            style={{ background: '#16a34a', color: '#fff' }}
            onClick={handlePublish}
            disabled={publishing || saving || courseCards.length === 0}
          >
            {publishing ? 'Creating...' : 'Create Bundle'}
          </button>
        </div>
      </header>

      <PublishStateNote mode="draft">
        <p>
          Edits here are held as a <strong>draft</strong>. Creating a bundle
          freezes a version (marks are included — see the review dialog), which
          goes live only after you{' '}
          <Link to={`/clubs/${clubId}/courses/history`}>activate</Link> it in
          Version History.
        </p>
      </PublishStateNote>

      {error && <div className="error-banner cc-error">{error}</div>}

      {/* Club-wide settings common home: course grid + default start/finish line.
          Consolidated from the old left-column "Grid setup" panel and the
          card-settings "Default Start/Finish" block. (ra-npo7) */}
      {showClubSettings && (
        <section className="cc-club-settings" aria-label="Club settings">
          <div className="cc-club-settings-head">
            <h3>Club Settings</h3>
            <p className="form-hint">
              Club-wide configuration, shared across every course card in this season.
            </p>
          </div>
          <div className="cc-club-settings-groups">
            <div className="cc-settings-group">
              <h4>Course grid</h4>
              <label className="form-field-inline">
                <span className="form-label">Wind directions:</span>
                <select
                  value={gridConfig.directionCount}
                  onChange={(e) => updateGridConfig({ directionCount: Number(e.target.value) })}
                >
                  {DIRECTION_COUNTS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <label className="form-field-inline">
                <span className="form-label">Default name:</span>
                <select
                  value={gridConfig.nameScheme}
                  onChange={(e) => updateGridConfig({ nameScheme: e.target.value })}
                >
                  {NAME_SCHEMES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label className="form-field-inline">
                <span className="form-label">Numbering:</span>
                <select
                  value={gridConfig.numbering}
                  onChange={(e) => updateGridConfig({ numbering: e.target.value })}
                >
                  {NUMBERING_MODES.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </label>
              <label className="form-field-inline">
                <span className="form-label">Courses / direction:</span>
                <input
                  type="number"
                  min="1"
                  max="9"
                  value={gridConfig.coursesPerDirection}
                  onChange={(e) => updateGridConfig({ coursesPerDirection: Number(e.target.value) })}
                  className="inline-input mark-input"
                />
              </label>
              <p className="form-hint">
                Example default name:{' '}
                <strong>{defaultCourseName(gridConfig, gridConfig.directionCount === 16 ? 'C' : 'A', gridConfig.numbering === 'global-gapped' ? 11 : 3)}</strong>
              </p>
              <button
                className="btn btn-sm btn-primary"
                onClick={handleSaveGridConfig}
                disabled={gridConfigSaving}
              >
                {gridConfigSaving ? 'Saving...' : 'Save grid setup'}
              </button>
            </div>

            <div className="cc-settings-group">
              <h4>Default Start/Finish</h4>
              <p className="form-hint">
                Club-wide default, applied to every card unless overridden. Saved
                independently with the button below (not with the season draft).
              </p>
              <LineConfigRow
                label="Start:"
                type={clubDefaults.defaultStartType}
                markA={clubDefaults.defaultStartMarkA}
                markB={clubDefaults.defaultStartMarkB}
                offsetNm={clubDefaults.defaultStartOffsetNm}
                onTypeChange={(v) => updateDefault({ defaultStartType: v })}
                onMarkAChange={(v) => updateDefault({ defaultStartMarkA: v })}
                onMarkBChange={(v) => updateDefault({ defaultStartMarkB: v })}
                onOffsetChange={(v) => updateDefault({ defaultStartOffsetNm: v })}
                offsetLabel="nm downwind"
              />
              <LineConfigRow
                label="Finish:"
                type={clubDefaults.defaultFinishType}
                markA={clubDefaults.defaultFinishMarkA}
                markB={clubDefaults.defaultFinishMarkB}
                offsetNm={clubDefaults.defaultFinishOffsetNm}
                onTypeChange={(v) => updateDefault({ defaultFinishType: v })}
                onMarkAChange={(v) => updateDefault({ defaultFinishMarkA: v })}
                onMarkBChange={(v) => updateDefault({ defaultFinishMarkB: v })}
                onOffsetChange={(v) => updateDefault({ defaultFinishOffsetNm: v })}
                offsetLabel="nm upwind"
              />
              <button
                className="btn btn-sm btn-primary"
                onClick={handleSaveDefaults}
                disabled={savingDefaults}
              >
                {savingDefaults ? 'Saving...' : defaultsSaved ? 'Saved' : 'Save default start/finish'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Pinned card header — title + lozenges at a glance, always visible; the
          Edit button jumps down to the editable card-settings block. (ra-npo7) */}
      {selectedCard && (
        <section className="cc-card-header" aria-label="Card summary">
          {/* Card list — moved here from the left column so the area below the
              header is the card itself. (ra-npo7 #1) */}
          <div className="cc-card-tabs">
            {courseCards.map((card, idx) => (
              <button
                key={card.id}
                className={`cc-card-tab ${idx === selectedCardIdx ? 'active' : ''}`}
                onClick={() => { setSelectedCardIdx(idx); setSelectedCell(null); setSelectedNamedCourseId(null); }}
              >
                {card.name || `Card ${idx + 1}`}
                <span className="cc-card-count">
                  {(card.courses || []).length}
                </span>
              </button>
            ))}
            <button
              type="button"
              className="cc-card-tab-add"
              onClick={handleAddCard}
              title="Add a new card"
            >
              + Card
            </button>
          </div>

          <div className="cc-card-header-body">
            <div className="cc-card-header-left">
              <div className="cc-card-header-main">
                <h2 className="cc-card-title">
                  {selectedCard.name || `Card ${selectedCardIdx + 1}`}
                </h2>
              </div>
              <div className="cc-card-header-lozenges">
                {/* Class NAMES only — start times are shown as rows on the RHS
                    (not lozenge-styled). (ra-npo7 #2) */}
                {(selectedCard.sailing_classes || []).map((sc) => (
                  <span key={sc.name} className="cc-lozenge cc-lozenge-static">
                    {sc.name}
                  </span>
                ))}
                {selectedCard.vhf && <span className="cc-chip">VHF {selectedCard.vhf}</span>}
                {(selectedCard.properties || [])
                  .filter((p) => (p.key || '').trim() || (p.value || '').trim())
                  .map((p, i) => (
                    <span key={i} className="cc-chip">
                      {p.key}{p.value ? `: ${p.value}` : ''}
                    </span>
                  ))}
              </div>
            </div>

            <div className="cc-card-header-right">
              <button
                type="button"
                className="btn btn-sm cc-card-edit-btn"
                onClick={scrollToCardSettings}
              >
                Edit
              </button>
              {/* Start times as multiple rows toward the RHS. (ra-npo7 #2) */}
              {(selectedCard.sailing_classes || []).some((sc) => sc.startTime) && (
                <div className="cc-card-header-starts">
                  {(selectedCard.sailing_classes || [])
                    .filter((sc) => sc.startTime)
                    .map((sc) => (
                      <div key={sc.name} className="cc-start-row">
                        <span className="cc-start-class">{sc.name}</span>
                        <span className="cc-start-time">{sc.startTime}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Main content area */}
      <div className="cc-main">
        {/* Left panel: card grids (the card list moved into the pinned header) */}
        <div className="cc-left">
          {/* Named courses — wind-sector grid with cursor-key navigation,
              mirroring the regular-course grid. (ra-dzqv) */}
          {selectedCard && (namedCourses.length > 0 || !hasGridCourses) && (
            <div className="cc-named-courses">
              <NamedCourseGrid
                courses={namedCourses}
                marks={marks}
                selectedCourseId={selectedNamedCourseId}
                onSelectCourse={handleSelectNamedCourse}
              />
              <button
                className="btn btn-sm cc-add-named-course"
                onClick={handleAddNamedCourse}
              >
                + Named Course
              </button>
            </div>
          )}

          {/* A-P direction grid (hidden when only named courses exist) */}
          {selectedCard && (hasGridCourses || !hasNamedCourses) && (
            <CardGrid
              courses={gridCourses}
              marks={marks}
              selectedCell={selectedCell}
              onSelectCell={handleSelectCell}
              cardLine={resolveCardLine(selectedCard, bundleDefaults)}
              gridConfig={gridConfig}
            />
          )}

          <div className="cc-left-actions">
            <button
              className="btn btn-sm"
              onClick={() => setShowAddPanel(!showAddPanel)}
            >
              {showAddPanel ? 'Hide' : 'Add Courses'}
            </button>
            {selectedCard && courseCards.length > 1 && (
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleRemoveCard(selectedCardIdx)}
              >
                Remove card
              </button>
            )}
          </div>

          {showAddPanel && (
            <div className="cc-add-panel">
              <button className="btn btn-sm" onClick={handleAddCard}>+ New Card</button>
              <button className="btn btn-sm" onClick={handleAddNamedCourse}>+ Named Course</button>
              <button className="btn btn-sm" onClick={() => setShowCardImport(true)}>
                Copy card from season
              </button>
              <button className="btn btn-sm" onClick={() => setShowMarkImport(true)}>
                Copy marks from season
              </button>
              <button
                className="btn btn-sm"
                onClick={() => setShowPasteImport(!showPasteImport)}
              >
                {showPasteImport ? 'Hide text import' : 'Import from text'}
              </button>
            </div>
          )}
        </div>

        {/* Right panel: editor + preview */}
        <div className="cc-right">
          {showPasteImport ? (
            <PasteImport
              card={selectedCard}
              marks={marks}
              onImport={handlePasteImport}
              onClose={() => setShowPasteImport(false)}
            />
          ) : selectedNamedCourseId && selectedCourse ? (
            <>
              <CourseEditor
                directionCode={null}
                courseNumber={0}
                courseName={selectedCourse.name}
                windDirection={selectedCourse.windDirection}
                startArea={selectedCourse.startArea}
                course={selectedCourse}
                marks={marks}
                markLookup={markLookup}
                onChange={handleCourseChange}
                onRemoveCourse={handleRemoveNamedCourse}
                onRenameCourse={(newName) => {
                  handleCourseChange({ ...selectedCourse, name: newName });
                }}
                onWindDirectionChange={(dir) => {
                  handleCourseChange({ ...selectedCourse, windDirection: dir });
                }}
                onStartAreaChange={(area) => {
                  handleCourseChange({ ...selectedCourse, startArea: area });
                }}
                resolvedCardLine={selectedCard ? resolveCardLine(selectedCard, bundleDefaults) : null}
              >
                <CoursePreview
                  marks={marks}
                  markIds={selectedCourse?.markIds}
                  markDirections={selectedCourse?.markDirections}
                  bearing={selectedCourse?.bearing}
                  startFinish={previewStartFinish}
                />
              </CourseEditor>
            </>
          ) : selectedCell ? (
            <>
              <CourseEditor
                directionCode={selectedCell.dir}
                courseNumber={selectedCell.num}
                course={selectedCourse}
                adjacentCourse={adjacentCourse}
                marks={marks}
                markLookup={markLookup}
                onChange={handleCourseChange}
                onNavigate={handleNavigate}
                prevNumber={gridNav.prev}
                nextNumber={gridNav.next}
                resolvedCardLine={selectedCard ? resolveCardLine(selectedCard, bundleDefaults) : null}
              >
                <CoursePreview
                  marks={marks}
                  markIds={selectedCourse?.markIds}
                  markDirections={selectedCourse?.markDirections}
                  bearing={selectedCourse?.bearing}
                  startFinish={previewStartFinish}
                />
              </CourseEditor>
            </>
          ) : (
            <div className="course-editor-empty">
              <p>{hasNamedCourses
                ? 'Select a course from the list to edit it, or add a new named course.'
                : 'Select a cell in the grid to edit a course variation.'
              }</p>
              <p className="form-hint">{hasNamedCourses
                ? 'Named courses use space-separated mark IDs (e.g. MUG KSH BRO).'
                : 'Each cell represents a wind direction + variation number. Click a cell or type a mark sequence to define the course.'
              }</p>
              {!hasNamedCourses && (
                <button className="btn btn-sm" onClick={handleAddNamedCourse}>
                  + Named Course (no wind direction)
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom panel: card metadata (Edit button in the pinned header jumps here) */}
      {selectedCard && (
        <div className="cc-bottom" ref={cardSettingsRef} id="cc-card-settings">
          <h3>Card Settings</h3>
          <div className="cc-meta-grid">
            <label className="form-field">
              <span className="form-label">Card name</span>
              <input
                type="text"
                value={selectedCard.name}
                onChange={(e) => updateCard(selectedCardIdx, { name: e.target.value })}
                placeholder="e.g. Saturday Hut Starts"
              />
            </label>
            <label className="form-field form-field-narrow">
              <span className="form-label">VHF Channel</span>
              <input
                type="text"
                value={selectedCard.vhf}
                onChange={(e) => updateCard(selectedCardIdx, { vhf: e.target.value })}
                placeholder="68"
              />
            </label>
          </div>

          {/* Sailing classes — lozenge picker */}
          <div className="cc-classes">
            <span className="form-label">Sailing Classes</span>
            <div className="cc-class-lozenges">
              {fleetClasses.map((cls) => {
                const active = (selectedCard.sailing_classes || []).some((sc) => sc.name === cls.name);
                return (
                  <button
                    key={cls.name}
                    type="button"
                    className={`cc-lozenge ${active ? 'cc-lozenge-active' : ''}`}
                    onClick={() => toggleSailingClass(cls)}
                  >
                    {cls.name}
                  </button>
                );
              })}
              <input
                type="text"
                className="inline-input cc-lozenge-input"
                placeholder="+ custom"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addCustomSailingClass(e.target.value);
                    e.target.value = '';
                  }
                }}
              />
            </div>
            {/* Start time rows for selected classes */}
            {(selectedCard.sailing_classes || []).map((sc, i) => (
              <div key={sc.name} className="cc-class-row">
                <span className="cc-class-name">{sc.name}</span>
                <input
                  type="text"
                  value={sc.flag || ''}
                  onChange={(e) => updateSailingClass(i, 'flag', e.target.value)}
                  placeholder="Flag"
                  className="inline-input cc-flag-input"
                />
                <input
                  type="time"
                  value={sc.startTime}
                  onChange={(e) => updateSailingClass(i, 'startTime', e.target.value)}
                  className="inline-input"
                />
              </div>
            ))}
          </div>

          {/* Card lozenges — up to 3 generic key-value properties (ra-hi9d) */}
          <CardPropertiesEditor
            properties={selectedCard.properties || []}
            onChange={(properties) => updateCard(selectedCardIdx, { properties })}
          />

          {/* Start/Finish line — this card. The club-wide DEFAULTS now live in the
              Club Settings home (ra-npo7); here you only override them per card. */}
          <div className="cc-line-config">
            <span className="form-label">Start/Finish line</span>
            {/* Repeat the inherited club default inline (ra-npo7 #3) so the user
                doesn't have to open Club Settings to see what this card inherits. */}
            <p className="form-hint cc-inherited-line">
              Club default (Club Settings) —{' '}
              <strong>Start:</strong>{' '}
              {describeLineConfig(bundleDefaults.defaultStartType, bundleDefaults.defaultStartMarkA, bundleDefaults.defaultStartMarkB, bundleDefaults.defaultStartOffsetNm, 'nm downwind')}
              {'  ·  '}
              <strong>Finish:</strong>{' '}
              {describeLineConfig(bundleDefaults.defaultFinishType, bundleDefaults.defaultFinishMarkA, bundleDefaults.defaultFinishMarkB, bundleDefaults.defaultFinishOffsetNm, 'nm upwind')}
              . Inherited unless overridden below.
            </p>
            {/* Card override toggle */}
            {(selectedCard.startType != null || selectedCard.finishType != null) ? (
              <div className="cc-card-override">
                <span className="form-label cc-override-label">Card override</span>
                <CardLineOverrideRow
                  label="Start:"
                  ownType={selectedCard.startType}
                  ownMarkA={selectedCard.startMarkA}
                  ownMarkB={selectedCard.startMarkB}
                  ownOffsetNm={selectedCard.startOffsetNm}
                  ownOffsetAngle={selectedCard.startOffsetAngle}
                  inheritedType={bundleDefaults.defaultStartType}
                  inheritedMarkA={bundleDefaults.defaultStartMarkA}
                  inheritedMarkB={bundleDefaults.defaultStartMarkB}
                  inheritedOffsetNm={bundleDefaults.defaultStartOffsetNm}
                  inheritedOffsetAngle={bundleDefaults.defaultStartOffsetAngle}
                  onClear={() => { updateCard(selectedCardIdx, { startType: null, startMarkA: null, startMarkB: null, startOffsetNm: null, startOffsetAngle: null }); }}
                  fieldPrefix="start"
                  offsetLabel="nm downwind"
                  updateCard={(updates) => updateCard(selectedCardIdx, updates)}
                />
                <CardLineOverrideRow
                  label="Finish:"
                  ownType={selectedCard.finishType}
                  ownMarkA={selectedCard.finishMarkA}
                  ownMarkB={selectedCard.finishMarkB}
                  ownOffsetNm={selectedCard.finishOffsetNm}
                  ownOffsetAngle={selectedCard.finishOffsetAngle}
                  inheritedType={bundleDefaults.defaultFinishType}
                  inheritedMarkA={bundleDefaults.defaultFinishMarkA}
                  inheritedMarkB={bundleDefaults.defaultFinishMarkB}
                  inheritedOffsetNm={bundleDefaults.defaultFinishOffsetNm}
                  inheritedOffsetAngle={bundleDefaults.defaultFinishOffsetAngle}
                  onClear={() => { updateCard(selectedCardIdx, { finishType: null, finishMarkA: null, finishMarkB: null, finishOffsetNm: null, finishOffsetAngle: null }); }}
                  fieldPrefix="finish"
                  offsetLabel="nm upwind"
                  updateCard={(updates) => updateCard(selectedCardIdx, updates)}
                />
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-xs cc-override-btn"
                onClick={() => updateCard(selectedCardIdx, {
                  // Seed both start AND finish from bundle defaults. Previously only
                  // start fields were copied, so clicking "Override" left finish
                  // still inherited — the finish row would show an orphan "Override"
                  // button alongside the cleared start row.
                  startType: bundleDefaults.defaultStartType,
                  startMarkA: bundleDefaults.defaultStartMarkA,
                  startMarkB: bundleDefaults.defaultStartMarkB,
                  startOffsetNm: bundleDefaults.defaultStartOffsetNm,
                  startOffsetAngle: bundleDefaults.defaultStartOffsetAngle,
                  finishType: bundleDefaults.defaultFinishType,
                  finishMarkA: bundleDefaults.defaultFinishMarkA,
                  finishMarkB: bundleDefaults.defaultFinishMarkB,
                  finishOffsetNm: bundleDefaults.defaultFinishOffsetNm,
                  finishOffsetAngle: bundleDefaults.defaultFinishOffsetAngle,
                })}
              >
                Override for this card
              </button>
            )}
          </div>

          {/* Footnotes */}
          <label className="form-field">
            <span className="form-label">Footnotes</span>
            <textarea
              value={selectedCard.footnotes}
              onChange={(e) => updateCard(selectedCardIdx, { footnotes: e.target.value })}
              placeholder="Optional footnotes for this card"
              rows={2}
            />
          </label>
        </div>
      )}

      {/* Action bar */}
      <div className="cc-actions">
        {saved && <span className="saved-badge">Draft saved</span>}
        <Link to={`/clubs/${clubId}/courses/history`} className="btn btn-sm">
          Version history
        </Link>
      </div>

      {/* Card import dialog */}
      {showCardImport && (
        <div className="confirm-overlay">
          <div className="confirm-dialog card-import-dialog">
            <h3>Copy card from season</h3>
            <label className="form-field">
              <span className="form-label">Season:</span>
              <select
                value={importSourceEvent}
                onChange={(e) => {
                  setImportSourceEvent(e.target.value);
                  loadImportSource(e.target.value);
                }}
              >
                <option value="" disabled>Select season...</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.id}</option>
                ))}
              </select>
            </label>
            {importLoading && <p>Loading cards...</p>}
            {importSourceCards && !importLoading && (
              importSourceCards.length === 0 ? (
                <p>No cards found in this season.</p>
              ) : (
                <div className="card-import-list">
                  <p className="form-label">Select a card to copy:</p>
                  {importSourceCards.length > 1 && (
                    <button
                      className="btn card-import-option card-import-all"
                      onClick={() => handleImportAllCards(importSourceCards)}
                    >
                      <strong>ALL cards</strong>
                      <span className="card-import-meta">
                        {importSourceCards.length} card{importSourceCards.length !== 1 ? 's' : ''}
                      </span>
                    </button>
                  )}
                  {importSourceCards.map((card, i) => (
                    <button
                      key={card.id || i}
                      className="btn card-import-option"
                      onClick={() => handleImportCard(card)}
                    >
                      <strong>{card.name || `Card ${i + 1}`}</strong>
                      <span className="card-import-meta">
                        {(card.courses || []).length} course{(card.courses || []).length !== 1 ? 's' : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )
            )}
            <div className="confirm-actions">
              <button className="btn" onClick={() => {
                setShowCardImport(false);
                setImportSourceEvent('');
                setImportSourceCards(null);
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Mark import dialog */}
      {showMarkImport && (
        <div className="confirm-overlay">
          <div className="confirm-dialog card-import-dialog">
            <h3>Copy marks from season</h3>
            <label className="form-field">
              <span className="form-label">Season:</span>
              <select
                value={markImportSourceEvent}
                onChange={(e) => {
                  setMarkImportSourceEvent(e.target.value);
                  loadMarkImportSource(e.target.value);
                }}
              >
                <option value="" disabled>Select season...</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.id}</option>
                ))}
              </select>
            </label>
            {markImportLoading && <p>Loading marks...</p>}
            {markImportSourceMarks && !markImportLoading && (
              markImportSourceMarks.length === 0 ? (
                <p>No marks found in this season.</p>
              ) : (
                <div className="mark-import-summary">
                  <p>
                    {markImportSummary.total} mark{markImportSummary.total !== 1 ? 's' : ''} found
                    {markImportSummary.newCount > 0 && <>, <strong>{markImportSummary.newCount} new</strong></>}
                    {markImportSummary.existingCount > 0 && `, ${markImportSummary.existingCount} already present`}
                  </p>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={markImportSummary.newCount === 0}
                    onClick={() => handleImportMarks(markImportSourceMarks)}
                  >
                    {markImportSummary.newCount > 0
                      ? `Copy ${markImportSummary.newCount} mark${markImportSummary.newCount !== 1 ? 's' : ''}`
                      : 'No new marks to copy'}
                  </button>
                </div>
              )
            )}
            <div className="confirm-actions">
              <button className="btn" onClick={() => {
                setShowMarkImport(false);
                setMarkImportSourceEvent('');
                setMarkImportSourceMarks(null);
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Diff review dialog */}
      {showDiffDialog && diffData && (
        <div className="confirm-overlay">
          <div className="confirm-dialog diff-dialog">
            <h3>Review changes before creating bundle</h3>
            <p className="diff-dialog-hint">
              This freezes a new bundle version (marks and cards). It is{' '}
              <strong>not live yet</strong> — activate it in Version History to
              publish to users, who see it the next time the app loads this club.
            </p>
            {diffData.isEmpty ? (
              <p>No changes detected — already in sync with latest bundle.</p>
            ) : (
              <div className="diff-summary">
                {diffData.cards.added.length > 0 && (
                  <div className="diff-section">
                    <strong>Cards added:</strong> {diffData.cards.added.join(', ')}
                  </div>
                )}
                {diffData.cards.removed.length > 0 && (
                  <div className="diff-section">
                    <strong>Cards removed:</strong> {diffData.cards.removed.join(', ')}
                  </div>
                )}
                {diffData.cards.modified.length > 0 && (
                  <div className="diff-section">
                    <strong>Cards modified:</strong>
                    <ul>
                      {diffData.cards.modified.map((m) => (
                        <li key={m.name}>
                          <strong>{m.name}</strong>: {m.changes.join('; ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {diffData.marks.added.length > 0 && (
                  <div className="diff-section">
                    <strong>Marks added:</strong> {diffData.marks.added.join(', ')}
                  </div>
                )}
                {diffData.marks.removed.length > 0 && (
                  <div className="diff-section">
                    <strong>Marks removed:</strong> {diffData.marks.removed.join(', ')}
                  </div>
                )}
                {diffData.marks.modified.length > 0 && (
                  <div className="diff-section">
                    <strong>Marks modified:</strong> {diffData.marks.modified.join(', ')}
                  </div>
                )}
                {diffData.defaults.length > 0 && (
                  <div className="diff-section">
                    <strong>Bundle defaults changed:</strong>
                    <ul>
                      {diffData.defaults.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="confirm-actions">
              <button className="btn" onClick={() => setShowDiffDialog(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ background: '#16a34a' }}
                onClick={handlePublishConfirm}
              >
                Confirm and Create Bundle
              </button>
            </div>
          </div>
        </div>
      )}

      {publishResult && (
        <div className="success-banner">
          Bundle created as <strong>{publishResult.bundleId}</strong> (v{publishResult.version}).
          Go to{' '}
          <Link to={`/clubs/${clubId}/courses/history`}>
            Version History
          </Link>
          {' '}to activate it.{' '}
          <Link to={`/clubs/${clubId}/events/${selectedEventId || clubId}/published/${publishResult.bundleId}`}>
            View bundle
          </Link>
        </div>
      )}
      </div>
    </>
  );
}
